import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Calendar, ChevronLeft, ChevronRight, LayoutDashboard, Plus, RefreshCw } from 'lucide-react';
import NewsletterDashboardView from './NewsletterDashboardView';
import PendingTasksDashboardPanel from '../Tasks/PendingTasksDashboardPanel';
import type { NewsletterDashboardRow } from '../../lib/newsletterDashboard';
import { toast } from 'sonner';
import { reportUiError } from '../../sentry';
import { useNavigate } from 'react-router-dom';
import { channelLabel, channelsToArray } from '../../lib/channels';
import CalendarDayDetailModal from './CalendarDayDetailModal';
import CalendarItemPreviewModal from './CalendarItemPreviewModal';
import {
  type CalendarItem,
  contentCalendarCardClass,
  contentCalendarLegendDotClass,
  kindLabel,
  kindPill,
  KIND_SORT,
  NEWSLETTER_STATUS_LEGEND,
  statusPill,
  newsletterStatusLabel,
} from './calendarShared';

type LayerPrefs = {
  showContent: boolean;
  showConnections: boolean;
  showTranscripts: boolean;
};

const LAYER_PREFS_KEY = 'lccto-calendar-layers';
const CALENDAR_TAB_KEY = 'lccto-calendar-tab';
const CALENDAR_VIEW_MODE_KEY = 'lccto-calendar-view-mode';

type CalendarTab = 'dashboard' | 'calendar';
type CalendarViewMode = 'month' | 'week';

function loadCalendarTab(): CalendarTab {
  try {
    const raw = localStorage.getItem(CALENDAR_TAB_KEY);
    return raw === 'dashboard' ? 'dashboard' : 'calendar';
  } catch {
    return 'calendar';
  }
}

function loadCalendarViewMode(): CalendarViewMode {
  try {
    return localStorage.getItem(CALENDAR_VIEW_MODE_KEY) === 'week' ? 'week' : 'month';
  } catch {
    return 'month';
  }
}

const DEFAULT_LAYER_PREFS: LayerPrefs = {
  showContent: true,
  showConnections: true,
  showTranscripts: true,
};

function loadLayerPrefs(): LayerPrefs {
  try {
    const raw = localStorage.getItem(LAYER_PREFS_KEY);
    if (!raw) return DEFAULT_LAYER_PREFS;
    const parsed = JSON.parse(raw) as Partial<LayerPrefs>;
    return {
      showContent: parsed.showContent ?? DEFAULT_LAYER_PREFS.showContent,
      showConnections: parsed.showConnections ?? DEFAULT_LAYER_PREFS.showConnections,
      showTranscripts: parsed.showTranscripts ?? DEFAULT_LAYER_PREFS.showTranscripts,
    };
  } catch {
    return DEFAULT_LAYER_PREFS;
  }
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, delta: number) {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

/** Week starts Sunday (matches month grid padding). */
function startOfWeek(d: Date) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - x.getDay());
  return x;
}

function addWeeks(d: Date, delta: number) {
  const x = new Date(d);
  x.setDate(x.getDate() + delta * 7);
  return startOfWeek(x);
}

/** Month view: 5 weeks with focal week on row 2 (index 1) — 1 week past, 3 weeks ahead. */
const MONTH_VIEW_GRID_ROWS = 5;
const MONTH_VIEW_FOCAL_ROW = 1;

function buildMonthGridDays(focalDate: Date): Date[] {
  const focalWeekStart = startOfWeek(focalDate);
  const gridStart = new Date(focalWeekStart);
  gridStart.setDate(gridStart.getDate() - MONTH_VIEW_FOCAL_ROW * 7);
  return Array.from({ length: MONTH_VIEW_GRID_ROWS * 7 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(d.getDate() + i);
    return d;
  });
}

function monthGridFocalDate(monthStart: Date): Date {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (monthStart.getTime() === startOfMonth(today).getTime()) {
    return today;
  }
  const lastDay = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
  return new Date(monthStart.getFullYear(), monthStart.getMonth(), Math.min(15, lastDay));
}

function weekRangeLabel(weekStart: Date) {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const sameMonth =
    weekStart.getMonth() === weekEnd.getMonth() && weekStart.getFullYear() === weekEnd.getFullYear();
  if (sameMonth) {
    const month = weekStart.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    return `${month} · ${weekStart.getDate()}–${weekEnd.getDate()}`;
  }
  const start = weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const end = weekEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${start} – ${end}`;
}

function yyyyMmDd(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dayLabel(d: Date) {
  return d.toLocaleDateString(undefined, { weekday: 'short' });
}

function monthLabel(d: Date) {
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function safeDateFromValue(val: string | null | undefined): Date | null {
  if (!val) return null;
  const dt = new Date(val);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function dateKeyFromValue(val: string | null | undefined): string | null {
  const dt = safeDateFromValue(val);
  if (!dt) return null;
  return yyyyMmDd(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()));
}

function connectionLabel(row: { name?: string | null; email?: string | null }): string {
  const name = typeof row.name === 'string' ? row.name.trim() : '';
  return name || (typeof row.email === 'string' ? row.email.trim() : '') || 'Connection';
}

export default function ContentCalendarView() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() =>
    loadCalendarViewMode() === 'week' ? startOfWeek(new Date()) : startOfMonth(new Date()),
  );
  const [loading, setLoading] = useState(true);
  const [contentRows, setContentRows] = useState<
    Array<{
      id: string | number;
      name: string | null;
      subtitle: string | null;
      status: string | null;
      scheduled_for: string | null;
      channels: unknown;
    }>
  >([]);
  const [connectionRows, setConnectionRows] = useState<
    Array<{
      id: string | number;
      email: string | null;
      name: string | null;
      last_contacted_at: string | null;
    }>
  >([]);
  const [transcriptRows, setTranscriptRows] = useState<
    Array<{
      id: string;
      title: string;
      summary: string | null;
      meeting_date: string | null;
    }>
  >([]);
  const [activeTab, setActiveTab] = useState<CalendarTab>(() => loadCalendarTab());
  const [viewMode, setViewMode] = useState<CalendarViewMode>(() => loadCalendarViewMode());
  const [layerPrefs, setLayerPrefs] = useState<LayerPrefs>(() => loadLayerPrefs());
  const [contentStatusFilter, setContentStatusFilter] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<CalendarItem | null>(null);
  const [dayDetail, setDayDetail] = useState<{ dateKey: string; items: CalendarItem[] } | null>(null);
  const [tasksRefreshKey, setTasksRefreshKey] = useState(0);

  const monthStart = useMemo(() => startOfMonth(month), [month]);
  const weekStart = useMemo(() => startOfWeek(month), [month]);
  const monthFocalDate = useMemo(() => monthGridFocalDate(monthStart), [monthStart]);

  const setCalendarViewMode = (mode: CalendarViewMode) => {
    setViewMode(mode);
    localStorage.setItem(CALENDAR_VIEW_MODE_KEY, mode);
    if (mode === 'week') {
      setMonth(startOfWeek(new Date()));
    } else {
      setMonth(startOfMonth(month));
    }
  };

  const updateLayerPrefs = (patch: Partial<LayerPrefs>) => {
    setLayerPrefs((prev) => {
      const next = { ...prev, ...patch };
      localStorage.setItem(LAYER_PREFS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const fetchRows = async () => {
    setLoading(true);
    const errors: string[] = [];

    try {
      const [contentResult, connectionsResult, transcriptsResult] = await Promise.all([
        supabase
          .from('newsletters')
          .select('id, name, subtitle, status, scheduled_for, channels')
          .limit(5000),
        supabase
          .from('subscribers')
          .select('id, email, name, last_contacted_at')
          .not('last_contacted_at', 'is', null)
          .limit(5000),
        supabase.from('transcripts').select('id, title, summary, meeting_date').limit(5000),
      ]);

      if (contentResult.error) {
        errors.push(`Content: ${contentResult.error.message}`);
        setContentRows([]);
      } else {
        setContentRows(contentResult.data ?? []);
      }

      if (connectionsResult.error) {
        errors.push(`Connections: ${connectionsResult.error.message}`);
        setConnectionRows([]);
      } else {
        setConnectionRows(connectionsResult.data ?? []);
      }

      if (transcriptsResult.error) {
        errors.push(`Transcripts: ${transcriptsResult.error.message}`);
        setTranscriptRows([]);
      } else {
        setTranscriptRows(transcriptsResult.data ?? []);
      }

      if (errors.length > 0) {
        reportUiError('content_calendar', 'load_partial', new Error(errors.join(' · ')));
        toast.error(errors.join(' · '));
      }
    } catch (e) {
      reportUiError('content_calendar', 'load', e);
      toast.error(e instanceof Error ? e.message : 'Failed to load calendar rows');
      setContentRows([]);
      setConnectionRows([]);
      setTranscriptRows([]);
    } finally {
      setLoading(false);
      setTasksRefreshKey((k) => k + 1);
    }
  };

  useEffect(() => {
    void fetchRows();
  }, []);

  const dashboardRows = useMemo((): NewsletterDashboardRow[] => {
    return contentRows.map((r) => ({
      id: String(r.id),
      name: typeof r.name === 'string' ? r.name : '',
      status: typeof r.status === 'string' ? r.status : 'draft',
      scheduled_for: r.scheduled_for,
      subtitle: r.subtitle,
      channels: r.channels,
    }));
  }, [contentRows]);

  const setTab = (tab: CalendarTab) => {
    setActiveTab(tab);
    localStorage.setItem(CALENDAR_TAB_KEY, tab);
  };

  const contentStatusCounts = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const r of contentRows) {
      if (!dateKeyFromValue(r.scheduled_for)) continue;
      const raw = typeof r.status === 'string' ? r.status.trim() : '';
      const key = raw.toLowerCase() || 'unknown';
      const label = raw || 'Unknown';
      const existing = counts.get(key);
      if (existing) existing.count += 1;
      else counts.set(key, { label, count: 1 });
    }
    return Array.from(counts.entries())
      .map(([key, v]) => ({ key, label: v.label, count: v.count }))
      .sort((a, b) => b.count - a.count);
  }, [contentRows]);

  useEffect(() => {
    if (contentStatusFilter && !contentStatusCounts.some((s) => s.key === contentStatusFilter)) {
      setContentStatusFilter(null);
    }
  }, [contentStatusFilter, contentStatusCounts]);

  const calendarItems = useMemo(() => {
    const items: CalendarItem[] = [];

    if (layerPrefs.showContent) {
      for (const r of contentRows) {
        const dateKey = dateKeyFromValue(r.scheduled_for);
        if (!dateKey) continue;
        const statusRaw = typeof r.status === 'string' ? r.status.trim() : '';
        const statusKey = statusRaw.toLowerCase() || 'unknown';
        if (contentStatusFilter && statusKey !== contentStatusFilter) continue;
        const entityId = String(r.id);
        items.push({
          id: `content-${entityId}`,
          entityId,
          kind: 'content',
          title: r.name || 'Untitled',
          subtitle: r.subtitle,
          status: r.status,
          dateKey,
          dateAt: r.scheduled_for,
          channels: r.channels,
        });
      }
    }

    if (layerPrefs.showConnections) {
      for (const r of connectionRows) {
        const dateKey = dateKeyFromValue(r.last_contacted_at);
        if (!dateKey) continue;
        const title = connectionLabel(r);
        const entityId = String(r.id ?? r.email ?? title);
        items.push({
          id: `connection-${entityId}`,
          entityId,
          kind: 'connection',
          title,
          subtitle: typeof r.email === 'string' ? r.email : null,
          status: null,
          dateKey,
          dateAt: r.last_contacted_at,
        });
      }
    }

    if (layerPrefs.showTranscripts) {
      for (const r of transcriptRows) {
        const dateKey = dateKeyFromValue(r.meeting_date);
        if (!dateKey) continue;
        const entityId = String(r.id);
        items.push({
          id: `transcript-${entityId}`,
          entityId,
          kind: 'transcript',
          title: r.title || 'Untitled transcript',
          subtitle: r.summary,
          status: null,
          dateKey,
          dateAt: r.meeting_date,
        });
      }
    }

    return items;
  }, [contentRows, connectionRows, transcriptRows, layerPrefs, contentStatusFilter]);

  const itemsByDay = useMemo(() => {
    const map: Record<string, CalendarItem[]> = {};
    for (const item of calendarItems) {
      (map[item.dateKey] ??= []).push(item);
    }
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => {
        const kindDiff = KIND_SORT[a.kind] - KIND_SORT[b.kind];
        if (kindDiff !== 0) return kindDiff;
        return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
      }),
    );
    return map;
  }, [calendarItems]);

  const gridDays = useMemo(() => {
    if (viewMode === 'week') {
      return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        return d;
      });
    }

    return buildMonthGridDays(monthFocalDate);
  }, [viewMode, monthFocalDate, weekStart]);

  const openPreview = (item: CalendarItem) => {
    setDayDetail(null);
    setPreviewItem(item);
  };

  const openDayDetail = (dateKey: string, items: CalendarItem[]) => {
    setPreviewItem(null);
    setDayDetail({ dateKey, items });
  };

  const anyLayerEnabled =
    layerPrefs.showContent || layerPrefs.showConnections || layerPrefs.showTranscripts;

  const isCurrentPeriod = useMemo(() => {
    const now = new Date();
    if (viewMode === 'week') {
      return weekStart.getTime() === startOfWeek(now).getTime();
    }
    return monthStart.getTime() === startOfMonth(now).getTime();
  }, [viewMode, monthStart, weekStart]);

  const periodLabel =
    viewMode === 'week' ? weekRangeLabel(weekStart) : monthLabel(monthStart);

  const todayKey = useMemo(() => yyyyMmDd(new Date()), []);
  const maxItemsPerDay = viewMode === 'week' ? 10 : 4;

  return (
    <div className="max-w-[90rem] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Calendar className="w-8 h-8 mr-3 text-emerald-500" />
            General calendar
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {activeTab === 'dashboard'
              ? 'Pipeline health, next-7-day actions, and distribution rhythm alongside the month grid.'
              : 'Month view keeps today on the second row (one week back, three ahead). Week view shows a single week. Filter by status or click an item for a preview.'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          {activeTab === 'calendar' ? (
            <>
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900/50 p-0.5">
            <button
              type="button"
              onClick={() => setCalendarViewMode('month')}
              className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'month'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Month
            </button>
            <button
              type="button"
              onClick={() => setCalendarViewMode('week')}
              className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'week'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Week
            </button>
          </div>
          <button
            type="button"
            onClick={() =>
              setMonth((m) => (viewMode === 'week' ? addWeeks(m, -1) : addMonths(m, -1)))
            }
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            aria-label={viewMode === 'week' ? 'Previous week' : 'Previous month'}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-900 dark:text-gray-100 min-w-[14rem] text-center">
            {periodLabel}
          </div>
          <button
            type="button"
            onClick={() =>
              setMonth((m) => (viewMode === 'week' ? addWeeks(m, 1) : addMonths(m, 1)))
            }
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            aria-label={viewMode === 'week' ? 'Next week' : 'Next month'}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() =>
              setMonth(viewMode === 'week' ? startOfWeek(new Date()) : startOfMonth(new Date()))
            }
            disabled={isCurrentPeriod}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-default"
            title={viewMode === 'week' ? 'Go to current week' : 'Go to current month'}
            aria-label={viewMode === 'week' ? 'Go to current week' : 'Go to current month'}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => void fetchRows()}
            disabled={loading}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => navigate('/outgoing-newsletters/new')}
            className="inline-flex items-center justify-center p-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
            title="New content"
            aria-label="New content"
          >
            <Plus className="w-5 h-5" />
          </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => void fetchRows()}
              disabled={loading}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 flex gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900/50 p-1 w-fit">
        <button
          type="button"
          onClick={() => setTab('dashboard')}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'dashboard'
              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <LayoutDashboard className="w-4 h-4" />
          Dashboard
        </button>
        <button
          type="button"
          onClick={() => setTab('calendar')}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'calendar'
              ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Calendar
        </button>
      </div>

      {activeTab === 'dashboard' ? (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_20rem] 2xl:grid-cols-[minmax(0,1fr)_22rem] gap-6 items-start">
          <NewsletterDashboardView
            rows={dashboardRows}
            loading={loading}
            onRefresh={() => void fetchRows()}
          />
          <PendingTasksDashboardPanel refreshKey={tasksRefreshKey} />
        </div>
      ) : null}

      {activeTab === 'calendar' ? (
      <>
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Show</span>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={layerPrefs.showContent}
            onChange={(e) => updateLayerPrefs({ showContent: e.target.checked })}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          Content (scheduled date)
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={layerPrefs.showConnections}
            onChange={(e) => updateLayerPrefs({ showConnections: e.target.checked })}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Connections (last contacted)
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={layerPrefs.showTranscripts}
            onChange={(e) => updateLayerPrefs({ showTranscripts: e.target.checked })}
            className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
          />
          Transcripts (meeting date)
        </label>
        {layerPrefs.showContent && contentStatusCounts.length > 0 ? (
          <>
            <span className="hidden sm:inline text-gray-300 dark:text-gray-600">|</span>
            <span className="w-full sm:w-auto text-sm font-medium text-gray-700 dark:text-gray-300">
              Content status
            </span>
            <button
              type="button"
              onClick={() => setContentStatusFilter(null)}
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                contentStatusFilter === null
                  ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Any
            </button>
            {contentStatusCounts.map((s) => {
              const active = contentStatusFilter === s.key;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setContentStatusFilter(active ? null : s.key)}
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    active
                      ? `${statusPill(s.label)} ring-2 ring-offset-1 ring-gray-900/80 dark:ring-white/90`
                      : `${statusPill(s.label)} hover:opacity-90`
                  }`}
                >
                  {s.label}
                  <span className="ml-1 opacity-80">{s.count}</span>
                </button>
              );
            })}
            <span className="hidden lg:inline text-gray-300 dark:text-gray-600">|</span>
            <span className="w-full lg:w-auto text-[10px] font-medium text-gray-500 dark:text-gray-400">
              Color key
            </span>
            {NEWSLETTER_STATUS_LEGEND.map((entry) => (
              <span
                key={entry.label}
                className="inline-flex items-center gap-1.5 text-[10px] text-gray-600 dark:text-gray-400"
                title={entry.hint}
              >
                <span
                  className={`w-2.5 h-2.5 rounded-sm shrink-0 ${contentCalendarLegendDotClass(entry.label)}`}
                  aria-hidden
                />
                {entry.label}
              </span>
            ))}
          </>
        ) : null}
      </div>

      {!anyLayerEnabled ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 px-4 py-10 text-center text-sm text-gray-600 dark:text-gray-400">
          Enable at least one layer above to populate the calendar.
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
            {Array.from({ length: 7 }).map((_, i) => {
              const d = new Date(2020, 10, 1 + i);
              return (
                <div key={i} className="px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {dayLabel(d)}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-7">
            {gridDays.map((d) => {
              const key = yyyyMmDd(d);
              const items = itemsByDay[key] ?? [];
              const inMonth = viewMode === 'week' || d.getMonth() === monthStart.getMonth();
              const isToday = key === todayKey;
              return (
                <div
                  key={key}
                  className={`${viewMode === 'week' ? 'min-h-[14rem]' : 'min-h-[9rem]'} border-t border-gray-200 dark:border-gray-700 p-2 ${
                    isToday
                      ? 'bg-emerald-50/80 dark:bg-emerald-950/25 ring-2 ring-inset ring-emerald-500/60'
                      : !inMonth
                        ? 'bg-gray-50/70 dark:bg-gray-900/30'
                        : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div
                      className={`text-xs font-semibold ${
                        isToday
                          ? 'flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 text-white'
                          : inMonth
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-gray-500 dark:text-gray-500'
                      }`}
                    >
                      {d.getDate()}
                    </div>
                    {items.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => openDayDetail(key, items)}
                        className="text-[11px] text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 underline-offset-2 hover:underline"
                        title="View all items for this day"
                      >
                        {items.length}
                      </button>
                    ) : null}
                  </div>
                  <div className="space-y-1.5">
                    {items.slice(0, maxItemsPerDay).map((item) => {
                      const channelKeys = item.kind === 'content' ? channelsToArray(item.channels) : [];
                      const visibleChannels = channelKeys.slice(0, 2);
                      const moreChannels = channelKeys.length - visibleChannels.length;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => openPreview(item)}
                          className={`w-full text-left rounded-lg border border-gray-200 dark:border-gray-700 px-2 py-1.5 hover:brightness-[0.98] dark:hover:brightness-110 ${
                            item.kind === 'content' && item.status
                              ? contentCalendarCardClass(item.status)
                              : 'bg-white/70 dark:bg-gray-800/60'
                          }`}
                          title={
                            item.kind === 'content' && item.status
                              ? `${item.title} — ${item.status}`
                              : (item.subtitle ?? item.title)
                          }
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-[11px] font-medium text-gray-900 dark:text-gray-100 truncate">
                              {item.title}
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              {item.kind !== 'content' ? (
                                <span
                                  className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${kindPill(item.kind)}`}
                                >
                                  {kindLabel(item.kind)}
                                </span>
                              ) : null}
                              {item.kind === 'content' && item.status ? (
                                <span
                                  className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${statusPill(item.status)}`}
                                >
                                  {newsletterStatusLabel(item.status)}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="mt-0.5 text-[10px] leading-snug text-gray-500 dark:text-gray-400 line-clamp-2 min-h-[1.75rem]">
                            {typeof item.subtitle === 'string' ? item.subtitle.trim() : ''}
                          </div>
                          {channelKeys.length > 0 ? (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {visibleChannels.map((chKey) => (
                                <span
                                  key={chKey}
                                  className="inline-flex max-w-full px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 truncate"
                                >
                                  {channelLabel(chKey)}
                                </span>
                              ))}
                              {moreChannels > 0 ? (
                                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                  +{moreChannels}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </button>
                      );
                    })}
                    {items.length > maxItemsPerDay ? (
                      <button
                        type="button"
                        onClick={() => openDayDetail(key, items)}
                        className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline"
                      >
                        +{items.length - maxItemsPerDay} more
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      </>
      ) : null}

      <CalendarDayDetailModal
        dateKey={dayDetail?.dateKey ?? null}
        items={dayDetail?.items ?? []}
        onSelectItem={openPreview}
        onClose={() => setDayDetail(null)}
      />
      <CalendarItemPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
    </div>
  );
}
